import { readFileSync, writeFileSync, mkdirSync } from 'node:fs';
import { join } from 'node:path';
import { CONFIG_DIR } from './config.js';
import { isCI } from './env.js';

/** npm package name — shared by the update nudge, the background refresh, and the `update` command. */
export const NPM_PACKAGE = 'rekor-cli';

/**
 * Command that prints the latest published version — shared by the `update` command and the
 * background refresh so the string and its shell-safety rationale live in one place. Run as a
 * string command (no args array) so Windows resolves the npm .cmd shim; it's fully static
 * (NPM_PACKAGE is a constant), so there's nothing to escape and no DEP0190 args-with-shell warning.
 */
export const NPM_VIEW_VERSION_CMD = `npm view ${NPM_PACKAGE} version`;

/** Cache file for the CLI (npm) update check. */
export const CLI_CACHE_FILE = 'update-check.json';
/** Cache file for the skill (GitHub) update check. */
export const SKILL_CACHE_FILE = 'skill-update-check.json';

// Staleness TTL — module-private; foreground/refresh code goes through isCacheStale/touchCache.
const MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * The env/CI opt-out for the whole update/skill machinery (nudge AND background refresh), shared by
 * the foreground and the refresher. The separate TTY gate — which suppresses a non-interactive run
 * entirely (no nudge, no refresh) — lives in the foreground (`showUpdateNudges`), not here.
 */
export function isUpdateCheckDisabled(): boolean {
  return Boolean(
    process.env['NO_UPDATE_NOTIFIER'] ||
    process.env['REKOR_NO_UPDATE_CHECK'] ||
    isCI(),
  );
}

export interface VersionCache {
  lastCheck: number;
  latest: string | null;
}

/** Read and shape-validate a version cache, or null if missing/corrupt. */
export function readCache(filename: string): VersionCache | null {
  try {
    const parsed = JSON.parse(readFileSync(join(CONFIG_DIR, filename), 'utf-8')) as VersionCache;
    if (typeof parsed.lastCheck !== 'number') return null;
    if (parsed.latest !== null && typeof parsed.latest !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCache(filename: string, cache: VersionCache): void {
  try {
    mkdirSync(CONFIG_DIR, { recursive: true });
    writeFileSync(join(CONFIG_DIR, filename), JSON.stringify(cache));
  } catch {
    // Cache write failures just mean we re-check next run — never fatal.
  }
}

/** True when no cache exists or it's older than the TTL — i.e. a background refresh is due. */
export function isCacheStale(filename: string): boolean {
  const cache = readCache(filename);
  return !cache || Date.now() - cache.lastCheck >= MAX_AGE_MS;
}

/**
 * Stamp `lastCheck` to now while preserving the known `latest`. Written as a sentinel right before
 * scheduling a background refresh, so concurrent/rapid foreground runs within the TTL don't each
 * spawn a refresher.
 */
export function touchCache(filename: string): void {
  writeCache(filename, { lastCheck: Date.now(), latest: readCache(filename)?.latest ?? null });
}
